import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import api, { formatErr } from "../api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Loader2, Leaf } from "lucide-react";
import { trackEvent } from "../lib/analytics";
import { getFirstTouch } from "../lib/acquisition";

export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshMe } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const match = (location.hash || "").match(/session_id=([^&]+)/);
    const sessionId = match ? match[1] : null;
    (async () => {
      if (!sessionId) {
        navigate("/login", { replace: true });
        return;
      }
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sessionId, acquisition: getFirstTouch() });
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
        await refreshMe();
        if (data.is_new) {
          trackEvent("sign_up", { method: "google" });
          trackEvent("registration_completed", { method: "google" });
          toast.success("Welcome to Backyard Buddy! Your free AI design is ready 🎁");
        } else {
          trackEvent("login", { method: "google" });
          toast.success(`Welcome back, ${data.user?.name?.split(" ")[0] || "gardener"}!`);
        }
        navigate("/ai-designer", { replace: true, state: { user: data.user } });
      } catch (e) {
        setError(formatErr(e));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#F9F6F0] flex items-center justify-center px-6" data-testid="auth-callback-page">
      <div className="text-center max-w-sm">
        <div className="w-12 h-12 rounded-full bg-[#2D4A3E] flex items-center justify-center mx-auto mb-5">
          <Leaf className="w-6 h-6 text-[#F9F6F0]" strokeWidth={1.5} />
        </div>
        {error ? (
          <>
            <p className="font-serif text-2xl font-light mb-2" data-testid="auth-callback-error">Sign-in didn&apos;t finish</p>
            <p className="text-sm text-[#5C6B62] font-light mb-6">{error}</p>
            <Link to="/login" className="btn-primary" data-testid="auth-callback-back-to-login">Back to sign in</Link>
          </>
        ) : (
          <>
            <Loader2 className="w-6 h-6 animate-spin text-[#2D4A3E] mx-auto mb-3" />
            <p className="font-serif text-2xl font-light">Signing you in…</p>
            <p className="text-sm text-[#5C6B62] font-light mt-1">Just a moment while we open the garden gate.</p>
          </>
        )}
      </div>
    </div>
  );
}
